<template>
  <div
    ref="dashboardAi"
    :class="['dashboard-ai', { 'no-data': noData }, {'load-data': isLoading || dashboardId ||isJamLack }]">
    <div v-if="isLoading" class="loading">
      <p class="loading-description" v-html="$t('dashboard.ai.loadingDescription')"></p>
      <i class="icon icon--dashboard-loading"></i>
      <p class="loading-title" v-html="$t('dashboard.ai.loadingTitle')"></p>
      <button type="button" class="dashboard-ai-btn create-cancel-btn" v-if="count > 0" @click="cancelReportCreation">
        <span class="time">
          {{count}}
        </span>
        {{ $t('dashboard.ai.reportCancelButton') }}
      </button>
    </div>
    <div v-else-if="dashboardId" class="complete-wrap">
      <i class="icon icon--becle-standing2"></i>
      <div class="button-wrap">
        <button @click="reset" class="dashboard-ai-btn">
          <i class="icon icon--reset-darkgray"></i>
          {{ $t('common.reset') }}
        </button>
        <button @click="openDashboard" class="dashboard-ai-btn">
          <i class="icon icon--check-darkgray"></i>
          {{ $t('dashboard.ai.view') }}
        </button>
      </div>
      <div class="complete-text">
        <span>{{ $t('dashboard.ai.toast') }}</span>
        <i class="icon icon--check-white"></i>
      </div>
    </div>
    <div v-else-if="isJamLack" class="jam-lack">
      <i class="icon icon--jam-bot"></i>
      <p class="jam-lack-description" v-html="$t('dashboard.ai.jamLack')"></p>
      <div class="remain-jam">
        <i class="icon icon--jam-with-bg"></i>
        <p>{{ $t('plan.myJam') }} : <span class="remain-jam-value">{{ remainJam | thousand }}</span></p>
      </div>
      <div class="btn-group">
      <button type="button" class="dashboard-ai-btn back-btn" @click="goBackPrompt">
        <i class="icon icon--back"></i>
        {{ $t('common.back') }}
      </button>
        <button type="button" class="dashboard-ai-btn charge-btn" @click="onChargeJam">
          <i class="icon icon--jam--16px"></i>
          <span v-html="$t('dashboard.ai.jamCharge')"></span>
        </button>
      </div>
    </div>
    <div v-else class="editor-wrap" :class="{expanded: expandTextArea ,  'is-mode': isShowMode}">
      <div class="icon-wrap">
        <i class="icon icon--dashboard-ai"></i>
        <i class="icon icon--becle-standing"></i>
      </div>
      <div v-if="domain.shared === 'shared'" class="shared">
        <i class="icon icon--bell-till"></i>
        <p class="shared-text">{{ $t('dashboard.ai.sharedReport') }}</p>
      </div>
      <template v-else>
        <div class="prompt-container">
          <Transition name="toast-fade" appear>
            <div v-if="isUngenerate"
                 ref="toast"
                 class="ungenerate_toast"
            >
              {{ $t('dashboard.ai.reportCancel') }}
            </div>
          </Transition>
          <div class="textarea-wrap"
               ref="textareaWrap"
               :style="expandTextArea ? { height: `calc(100% - ${animatedHeight}px)` } : {height: 44 + 'px'}"
          >
            <div
              v-if="showExpandedPlaceholder"
              class="expanded-placeholder"
              @click.stop="activatePromptInput"
            >
              <span
                v-html="expandedPlaceholderText"
                @mouseenter="showSegmentGuide"
                @mouseleave="hideSegmentGuide"
              ></span>
            </div>
              <ui-input-text :type="'textarea'"
                             ref="promptInput"
                             :rows="1"
                             :placeholder="promptPlaceholder"
                             :modelValue="userPrompt"
                             @input="userPrompt = $event"
                             @onFocus="expandTextArea = true"
                             @onBlur="onBlurTextArea">
              </ui-input-text>
            <div class="remain-jam">
              <i class="icon icon--jam-with-bg"></i>
              <p>{{ $t('plan.myJam') }} : <span class="remain-jam-value">{{ remainJam | thousand }}</span></p>
            </div>
          </div>
          <div class="create-button-wrap"
               v-if="expandTextArea"
               ref="createBtnWrap"
               :style="{ height: animatedHeight + 'px' }">
            <transition name="cross-fade">
              <template v-if="!isShowMode">
                <button key="create" class="create-button" @mouseenter="isShowMode = true" :disabled="!userPrompt.length">
                  <i class="icon icon--create-dashboard"></i>
                  <span class="btn-text">{{ $t('dashboard.ai.create') }}</span>
                </button>
              </template>

              <template v-else>
                <DashboardCreateButton
                  key="mode"
                  :isShowMode="isShowMode"
                  :remainJam="remainJam"
                  :reportJamPrice="reportJamPrice"
                  @requestPayment="requestPayment"
                  @startAnimation="startAnimation"
                />
              </template>
            </transition>
          </div>
        </div>
        <button class="question-guide-button" @click="isQuestionGuideVisible = true">
          {{ $t('dashboard.ai.questionGuide')}}
        <i class="icon icon--info-ourline-transparent"></i>
        </button>
      </template>
    </div>
    <div
      v-show="activeMode && (activeMode.dashboard || activeMode.dashboardHell) && !isJamLack"
      :class="['particle', activeMode && activeMode.dashboard ? 'dashboard' : 'dashboardHell']"
    >
      <ParticleSVGShort/>
    </div>
    <dimmed v-if="isQuestionGuideVisible">
      <dashboard-ai-guides @close="isQuestionGuideVisible = false" @updatePrompt="setUserPrompt" />
    </dimmed>
  </div>
</template>
<script>
import { parse, format } from 'date-fns'
import { mapState } from 'vuex'
import { create } from 'vue-modal-dialogs'
import { cancelDashboardReport, createDashboard, getAIReportData } from '@/api/ai'
import { getRemainJam } from '@/api/jam'
import { saveReportPayment } from '@/api/card'
import { REPORT_LINK } from '@/libs/constants'
import uiInputText from '@/components/ui/UiInputText'
import AppDialog from '@/components/ui/AppDialog'
import Dimmed from '@/components/ui/Dimmed'
import DashboardAiGuides from '@/components/dashboard/DashboardAiGuides'
import DashboardCreateButton from '@/components/dashboard/DashboardCreateButton.vue'
import { EventBus } from '@/libs/event-bus'
import ParticleSVGShort from '@/components/dashboard/ParticleSVGShort.vue'

const openDialog = create(AppDialog, 'dialogType')
const REPORT_DASHBOARD_JAM = 10
const REPORT_DASHBOARD_HELL_JAM = 30

export default {
  name: 'DashboardAi',
  components: {
    ParticleSVGShort,
    DashboardCreateButton,
    uiInputText,
    DashboardAiGuides,
    Dimmed
  },
  props: {
    dashboardData: {
      type: Object,
      default: () => { return {} }
    },
    noData: {
      type: Boolean,
      default: false
    },
    basicInfo: {
      type: Object,
      default: () => { return {} }
    },
    category: {
      type: String,
      required: false
    }
  },
  data () {
    return {
      pollingTimer: null,
      isUngenerate: false,
      toastTimer: null,
      ignoreOutsideOnce: false,
      activeMode: null,
      isJamLack: false,
      count: 6,
      countdownTimer: null,
      isShowMode: false,
      isLoading: false,
      remainJam: 0,
      dashboardId: null,
      userPrompt: '',
      expandTextArea: false,
      isJamTooltipVisible: false,
      reportJamPrice: {
        dashboard: REPORT_DASHBOARD_JAM,
        dashboardHell: REPORT_DASHBOARD_HELL_JAM
      },
      reportPaymentId: null,
      isQuestionGuideVisible: false,
      animatedHeight: 0,
      showExpandedPlaceholder: false,
      expandedPlaceholderTimer: null,
      hasActivatedPromptInput: false
    }
  },
  computed: {
    ...mapState([
      'journeyOptions',
      'journeyPeriod',
      'userData',
      'domain'
    ]),
    promptPlaceholder () {
      if (!this.expandTextArea) {
        return this.$t('dashboard.ai.placeholder')
      }

      return this.showExpandedPlaceholder
        ? ''
        : this.$t('dashboard.ai.placeholder')
    },
    expandedPlaceholderText () {
      return this.$t('dashboard.ai.placeholderExpanded', {
        startDate: this.getLocalizedJourneyDate(this.journeyPeriod.start),
        endDate: this.getLocalizedJourneyDate(this.journeyPeriod.end)
      })
    }
  },
  watch: {
    domain (value) {
      this.isLoading = false
      this.reportPaymentId = null
      this.dashboardId = null
      this.userPrompt = ''
    },
    userPrompt (newVal) {
      if (!newVal || newVal.trim() === '') {
        this.isShowMode = false
        this.updateExpandedPlaceholderVisibility(this.expandTextArea)
      } else {
        this.clearExpandedPlaceholderTimer()
        this.showExpandedPlaceholder = false
      }
    },
    expandTextArea (value, oldValue) {
      if (value && !oldValue) {
        this.hasActivatedPromptInput = false
      }
      if (!value) {
        this.hasActivatedPromptInput = false
      }
      this.updateExpandedPlaceholderVisibility(value)
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.setAutoHeight()
        })
      })
    },
    isShowMode () {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          this.setAutoHeight()
        })
      })
    },
    isLoading (val) {
      if (val) {
        this.startCountdown()
      } else {
        this.clearCountdown()
      }
    }
  },
  mounted () {
    this.setRemainJam()
    this.$nextTick(() => {
      requestAnimationFrame(() => {
        this.setAutoHeight()
      })
    })

    const dashboard = this.$refs.dashboardAi

    dashboard.addEventListener('animationend', this.onActiveEnd)
    window.addEventListener('resize', this.setAutoHeight)
    document.addEventListener('click', this.handleOutsideClick)
  },
  beforeDestroy () {
    const dashboard = this.$refs.dashboardAi
    dashboard.addEventListener('animationend', this.onActiveEnd)
    window.removeEventListener('resize', this.setAutoHeight)
    document.removeEventListener('click', this.handleOutsideClick)
    clearTimeout(this.toastTimer)
    this.clearExpandedPlaceholderTimer()
    this.clearCountdown()
  },
  methods: {
    clearExpandedPlaceholderTimer () {
      clearTimeout(this.expandedPlaceholderTimer)
      this.expandedPlaceholderTimer = null
    },
    updateExpandedPlaceholderVisibility (shouldShow) {
      this.clearExpandedPlaceholderTimer()

      if (!shouldShow || this.userPrompt.length || this.hasActivatedPromptInput) {
        this.showExpandedPlaceholder = false
        return
      }

      this.showExpandedPlaceholder = false
      this.expandedPlaceholderTimer = setTimeout(() => {
        this.showExpandedPlaceholder = true
      }, 120)
    },
    getLocalizedJourneyDate (dateStr) {
      if (!dateStr) return ''

      const date = parse(dateStr)

      switch (this.$i18n.locale) {
        case 'ko':
          return format(date, 'YYYY년 M월 D일')
        case 'ja':
          return format(date, 'YYYY年M月D日')
        case 'en':
        default:
          return format(date, 'MMMM D, YYYY')
      }
    },
    showUngenerateToast (e) {
      if (e) e.stopPropagation()

      this.ignoreOutsideOnce = true
      this.isUngenerate = true

      setTimeout(() => {
        this.ignoreOutsideOnce = false
      }, 0)

      clearTimeout(this.toastTimer)
      this.toastTimer = setTimeout(() => {
        this.isUngenerate = false
      }, 3000)
    },
    handleOutsideClick (e) {
      if (this.ignoreOutsideOnce) return

      const toast = this.$refs.toast
      if (!toast) return

      this.isUngenerate = false
      this.ignoreOutsideOnce = false
    },
    showSegmentGuide () {
      EventBus.$emit('dashboardAi:showSegmentGuide')
    },
    hideSegmentGuide () {
      EventBus.$emit('dashboardAi:hideSegmentGuide')
    },
    activatePromptInput () {
      this.hideSegmentGuide()
      this.clearExpandedPlaceholderTimer()
      this.hasActivatedPromptInput = true
      this.showExpandedPlaceholder = false
      this.$nextTick(() => {
        const inputComponent = this.$refs.promptInput
        const textarea = inputComponent && inputComponent.$refs && inputComponent.$refs.input
        if (textarea) {
          textarea.focus()
        }
      })
    },
    onChargeJam () {
      EventBus.$emit('onChargeJam', this.remainJam)
    },
    onActiveEnd (e) {
      if (e.target !== e.currentTarget) return

      if (!e.animationName.includes('pulse-weak')) return

      this.activeMode = null
    },
    startAnimation (mode) {
      this.activeMode = mode
      this.expandTextArea = false
      this.isShowMode = false
    },
    cancelReportCreation () {
      cancelDashboardReport({
        reportId: this.dashboardId
      }).then(() => {
        this.showUngenerateToast()

        if (this.pollingTimer) {
          clearTimeout(this.pollingTimer)
          this.pollingTimer = null
        }
        this.setRemainJam()
        this.reportPaymentId = null
        this.isJamLack = false
        this.isLoading = false
        this.dashboardId = false
        this.expandTextArea = true
        this.isShowMode = true
        this.activeMode = null
      })
    },
    goBackPrompt () {
      this.isJamLack = false
      this.dashboardId = false
      this.expandTextArea = true
      this.isShowMode = true
      this.activeMode = null
    },
    startCountdown () {
      this.clearCountdown()
      this.count = 6

      this.countdownTimer = setInterval(() => {
        this.count--

        if (this.count <= 0) {
          this.clearCountdown()
        }
      }, 1000)
    },
    clearCountdown () {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer)
        this.countdownTimer = null
      }
    },
    setAutoHeight () {
      const el = this.$refs.createBtnWrap
      let maxHeight = 38

      if (!el) return

      const btns = el.querySelectorAll('.mode-button')

      if (btns.length > 0 && this.isShowMode) {
        btns.forEach(btn => {
          btn.style.height = ''
        })

        const heights = [...btns].map(btn => btn.offsetHeight)
        maxHeight = Math.max(...heights)
        btns.forEach(btn => {
          btn.style.height = maxHeight + 'px'
        })
      } else {
        maxHeight = 38
      }

      this.animatedHeight = maxHeight
    },
    setUserPrompt (prompt) {
      this.userPrompt = prompt
      this.expandTextArea = true
      this.clearExpandedPlaceholderTimer()
      this.hasActivatedPromptInput = true
      this.showExpandedPlaceholder = false
    },
    setRemainJam () {
      const payload = {
        user_id: this.userData.userId,
        user_num: this.userData.userNum
      }
      return getRemainJam(payload, true).then(res => {
        this.remainJam = res.paidCount + res.freeCount
      }).catch(err => {
        console.log(err)
      })
    },
    async createDashboard (type) {
      this.isLoading = true
      this.expandTextArea = false
      createDashboard({
        category: this.category || type,
        prompt: this.userPrompt,
        dashboardData: { ...this.dashboardData, device: this.basicInfo },
        reportPaymentId: this.reportPaymentId,
        reportPaymentType: 'jam',
        paid: true,
        segment: {
          ...this.journeyOptions,
          start: this.journeyPeriod.start,
          end: this.journeyPeriod.end,
          parentSid: this.domain.parentSid,
          domain: this.domain.domain,
          lang: this.$i18n.locale
        }
      }).then((res) => {
        this.dashboardId = res.id
        this.checkReport(res.id)
      }).catch((err) => {
        this.isLoading = false
        this.reset()
        if (err.message === 'sysError') {
          openDialog('report.reportRequest.sysError')
        }
      })
    },
    checkReport (id) {
      if (this.isUngenerate) return

      getAIReportData(id, true)
        .then((res) => {
          if (res.reportStatus === 'ing') {
            this.pollingTimer = setTimeout(() => {
              this.checkReport(id)
            }, 5000)
          } else {
            this.isLoading = false
          }
        })
        .catch((e) => {
          this.isLoading = false
          console.log(e)
        })
    },
    reset () {
      this.setRemainJam()
      this.reportPaymentId = null
      this.dashboardId = null
      this.userPrompt = ''
      this.isJamLack = false
      this.activeMode = null
    },
    openDashboard () {
      window.open(REPORT_LINK + '/' + this.dashboardId, '_blank')
    },
    onBlurTextArea () {
      if (this.userPrompt.length === 0) this.expandTextArea = false
    },
    async requestPayment (type) {
      await this.setRemainJam()
      if (this.remainJam < this.reportJamPrice[type]) {
        this.isJamLack = true
        return
      }

      const params = {
        reportType: type,
        variantKey: 'jam',
        serviceType: 'ba',
        price: this.reportJamPrice[type]
      }
      saveReportPayment(params)
        .then(resp => {
          if (resp) {
            this.reportPaymentId = resp._id
            this.createDashboard(type)
          } else {
            this.reset()
          }
        }).catch(err => {
          console.log(err)
          this.reset()
        })
    }
  }
}
</script>
<style lang="scss" scoped>
.toast-fade-enter-active,
.toast-fade-leave-active,
.toast-fade-appear-active {
  transition: opacity 0.3s ease;
}

.toast-fade-enter-from,
.toast-fade-leave-to,
.toast-fade-appear-from {
  opacity: 0;
}
@keyframes scalePulse {
  0% {
    transform: scale(1);
  }

  20% {
    transform: scale(1.12);
  }

  40% {
    transform: scale(0.97);
  }

  65% {
    transform: scale(1.04);
  }

  100% {
    transform: scale(1);
  }
}
@keyframes textareaExpandUp {
  from {
    transform: translateY(38px);
  }
  to {
    transform: translateY(0);
  }
}

.cross-fade-enter-active,
.cross-fade-leave-active {
  position: absolute;
  inset: 0;
  transition:
    opacity .18s cubic-bezier(.22,.61,.36,1),
    transform .18s cubic-bezier(.22,.61,.36,1);
}

/* 들어올 때는 "아래"에서 시작 */
.cross-fade-enter {
  opacity: 0;
  transform: translateY(20px); /* 아래 */
}

/* 나갈 때는 살짝 위로 */
.cross-fade-leave-to {
  opacity: 0;
  transform: translateY(20px); /* 아래 */
}
.dashboard-ai {
  z-index: 1;
  margin-top: auto;

  .particle {
    position: absolute;
    pointer-events: none;
    width: 63%;
    min-width: 300px;
    bottom: -106px;

    &.dashboard {
      left: -61px;
    }

    &.dashboardHell {
      right: -61px;
    }

  }

  &.load-data {
    margin-top: 0;
    height: 100%;
  }
  &.no-data {
    pointer-events: none;
    .editor-wrap {
      .question-guide-button {
        opacity: 0.3;
      }

      .textarea-wrap {
        opacity: 0.3;
      }
    }
  }
  .loading {
    margin-top: 40px;
    @include flexXYcenter;
    flex-direction: column;

    color: #FFF;
    font-weight: 500;
    line-height: 150%;
    text-align: center;

    .icon {
      width: 90px;
      height: 90px;
    }
    .loading-title {
      font-size: 14px;
    }
    .loading-description {
      margin-bottom: 16px;
      font-size: 13px;
      line-height: 1.5;
    }

    .create-cancel-btn  {
      margin-top: 30px;

      .time {
        animation: scalePulse 1s ease-in-out 6;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 16px;
        height: 16px;
        line-height: 1;
        border-radius: 50em;
        background-color: #2F2F2F;
        color: #FFF;
        font-size: 11px;
      }
    }
  }

  .jam-lack {
    margin-top: 47px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    .jam-lack-description {
      color: #FFF;
      font-size: 14px;
      font-weight: 600;
      line-height: 150%;
      margin: 14px 0 8px;
    }

    .btn-group {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-top: 18px;
    }

    .charge-btn {
      & /deep/ {
        .accent {
          color: #FC6A00
        }
      }
    }

  }

  .remain-jam {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #fff;
    font-size: 13px;
    .remain-jam-value {
      font-weight: 600;
    }
  }
  .dashboard-ai-btn  {
    padding: 0 12px 0 8px;
    display: flex;
    align-items: center;
    gap: 6px;
    border-radius: 6px;
    background-color: #FFF;
    box-shadow: 0 5px 6px -2px rgba(0, 0, 0, 0.16);
    height: 38px;
    color: #222;
    font-size: 14px;
    font-weight: 600;
    line-height: 100%;
    transition: background-color 0.3s ease-in-out;

    &:hover {
      background-color: #EBEBEB;
    }
  }

  .complete-wrap {
    @include flexXYcenter;
    flex-direction: column;
    gap: 17px;
    margin-top: 80px;
    text-align: center;
    .button-wrap {
      display: flex;
      gap: 8px;
      button {
        .icon {
          width: 16px;
          height: 16px;
          background-size: contain;
        }
        &:hover {
          background-color: #F4F4F4;
        }
        &:active {
          background-color: #E6E6E6;
        }
      }
    }
    .complete-text {
      position: absolute;
      top: 49%;
      left: 50%;
      transform: translateX(-50%);

      display: flex;
      height: 32px;
      padding: 6px 10px;
      align-items: center;
      gap: 20px;
      border-radius: 6px;
      background: #000;
      box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.16);
      box-sizing: border-box;
      span {
        color: #FFF;
        font-size: 13px;
        line-height: 120%;
        white-space: nowrap;
      }
    }
  }
  .editor-wrap {
    .icon-wrap {
      position: absolute;
      top: 60px;
      left: 50%;
      transform: translateX(-50%);
    }
    .icon {
      &--becle-standing {
        position: absolute;
        top: 75px;
        right: -14px;
      }
    }
    .description {
      position: absolute;
      top: 233px;
      left: 50%;
      transform: translateX(-50%);
      width: 100%;
      color: #fff;
      font-size: 14px;
      font-weight: 500;
      line-height: 150%;

    }

    .prompt-container {
      display: flex;
      flex-direction: column;
      justify-content: flex-end;
      gap: 8px;
      position: relative;
      z-index: 1;
      .ungenerate_toast {
        height: 32px;
        cursor: pointer;
        z-index: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        position: absolute;
        top: 98px;
        left: 50%;
        line-height: 1;
        transform: translateX(-50%);
        padding: 0 16px;
        border-radius: 6px;
        border: 1px solid rgba(255, 255, 255, 0.24);
        background: #000;
        box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.16);
        color: #FFD900;
        text-align: center;
        font-size: 13px;
        font-weight: 400;
        line-height: 120%;
      }
    }

    .textarea-wrap {
      position: relative;
      transform: translateY(0);
      width: 100%;
      height: 44px;
      padding: 12px 12px 11px;
      border-radius: 8px;
      outline: 1px solid rgba(255, 255, 255, 0.12);
      background: #141414;
      box-shadow: 0 6px 10px 0 rgba(0, 0, 0, 0.24);
      box-sizing: border-box;
      transition: height 0.3s ease, padding 0.3s ease;

      .expanded-placeholder {
        position: absolute;
        inset: 12px 12px 50px 12px;
        z-index: 2;
        color: #bbb;
        font-size: 14px;
        line-height: 1.5;
        white-space: pre-line;
        pointer-events: auto;
        cursor: text;
        animation: expandedPlaceholderFadeIn 0.2s ease-out;

        span {
          display: inline-block;
          max-width: 100%;
          pointer-events: auto;
          cursor: text;
        }
      }

      & /deep/ .input--wrap {
        width: 100%;
        height: 100%;
        margin: 0;
        .label {
          display: none;
        }
        .input-element {
          height: 100%;
          textarea {
            height: 100%;
            padding: 0;
            border: none;
            border-radius: 0;
            background: none;
            color: #fff;
            line-height: 1.5;
            overflow: hidden;
            &::placeholder {
              color: #BBB;
            }
          }
          .icon--close14 {
            display: none;
          }
        }
      }
    }

    .create-button-wrap {
      position: relative;
      height: 38px;
      transition: height .2s ease;
      .create-button {
        @include flexXYcenter;
        position: relative;
        width: 100%;
        height: 38px;
        gap: 8px;
        color: #222;
        border-radius: 6px;
        background: #FFF;
        box-shadow: 0 5px 6px -2px  rgba(0, 0, 0, 0.16);
        font-size: 16px;
        font-weight: 600;
        line-height: 100%;
        padding-inline: 10px 12px;
        transition: opacity 0.25s ease, transform 0.35s cubic-bezier(0.25, 0.8, 0.5, 1);
        z-index: 1;
        box-sizing: border-box;
      }
    }
    .remain-jam {
      position: absolute;
      top: -28px;
      right: 9px;
    }
    &.expanded {
      .textarea-wrap {
        //height: 226px;
        padding-bottom: 50px;
        animation: textareaExpandUp 0.35s ease-out forwards;
      }

      .create-button {
        &:disabled {
          pointer-events: none;
          opacity: 0.3;
        }
      }

      .prompt-container {
        height: 274px;
      }
    }
    &.is-mode {
      .textarea-wrap {
        height: 209px;
      }
      .prompt-container {
        justify-content: flex-start;
      }
    }
    .question-guide-button {
      position: absolute;
      top: 16px;
      right: 16px;
      color: #fff;
      font-size: 13px;
      font-weight: 600;
      line-height: 1;
      height: 32px;
      padding-inline: 12px 10px;
      border-radius: 5px;
      border: 1px solid rgba(255, 255, 255, 0.44);
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 4px;
      &:hover {
        background-color: rgba(255, 255, 255, 0.1);
      }
    }

    .shared {
      @include flexXYcenter;
      margin-bottom: 26px;
      gap: 4px;
      .icon--bell-till {
        background-color: white;
        border-radius: 50%;
      }
      color: #FFF;
      font-size: 13px;
      line-height: 150%;
    }
  }

  & /deep/ {
    .mo {display: none}
  }
}

@media (max-width: 1492px) {
  .dashboard-ai {
    & /deep/ {
      .mo {display: block}
    }
  }
}

@keyframes expandedPlaceholderFadeIn {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
</style>
